import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getClassroomMembers } from '../lib/supabaseClassroomAPI';

export default function ManageClassroom() {
  const { classroomId } = useParams();
  const navigate = useNavigate();

  // Data
  const [classroom, setClassroom] = useState(null);
  const [students, setStudents] = useState([]); // [{ user_id, profiles:{username,role} }]
  const [lessons, setLessons] = useState([]);   // all my lessons
  const [folders, setFolders] = useState([]);   // my folders
  const [assigned, setAssigned] = useState([]); // lesson ids assigned to classroom

  // UI State
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedFolders, setExpandedFolders] = useState({});
  const [copied, setCopied] = useState(false);

  // Progress map: { [student_id]: { [lesson_id]: bool } }
  const [progress, setProgress] = useState({});

  // Derived
  const uncategorized = useMemo(
    () => lessons.filter(l => !l.folder_id),
    [lessons]
  );
  const assignedLessons = useMemo(
    () => lessons.filter(l => assigned.includes(l.id)),
    [lessons, assigned]
  );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError('');

        // Classroom
        const { data: cls, error: classErr } = await supabase
          .from('classrooms')
          .select('id, name, code, host_id')
          .eq('id', classroomId)
          .maybeSingle();
        if (classErr || !cls) throw classErr || new Error('Classroom not found');
        if (!mounted) return;
        setClassroom(cls);

        // Members
        const members = await getClassroomMembers(classroomId);
        const studentList = members.filter(m => m.profiles?.role === 'student');
        if (!mounted) return;
        setStudents(studentList);

        // My folders + lessons (owner is current host)
        const [{ data: myLessons, error: lErr }, { data: myFolders, error: fErr }] = await Promise.all([
          supabase.from('lessons').select('id, title, level, folder_id').eq('owner_id', cls.host_id),
          supabase.from('lesson_folders').select('id, name').eq('owner_id', cls.host_id),
        ]);
        if (lErr || fErr) throw lErr || fErr;
        if (!mounted) return;
        setLessons(myLessons || []);
        setFolders(myFolders || []);
        setExpandedFolders(
          Object.fromEntries((myFolders || []).map(f => [f.id, false]))
        );

        // Assigned lessons
        const { data: clsLessons, error: clErr } = await supabase
          .from('classroom_lessons')
          .select('lesson_id')
          .eq('classroom_id', classroomId);
        if (clErr) throw clErr;
        if (!mounted) return;
        setAssigned((clsLessons || []).map(r => r.lesson_id));
      } catch (e) {
        console.error(e);
        if (mounted) setError('Failed to load classroom data.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [classroomId]);

  useEffect(() => {
    let mounted = true;
    const loadProgress = async () => {
      try {
        setLoadingProgress(true);
        const { data, error } = await supabase
          .from('classroom_lesson_progress')
          .select('student_id, lesson_id, completed')
          .eq('classroom_id', classroomId);
        if (error) throw error;

        const map = {};
        (data || []).forEach(r => {
          if (!map[r.student_id]) map[r.student_id] = {};
          map[r.student_id][r.lesson_id] = !!r.completed;
        });
        if (mounted) setProgress(map);
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoadingProgress(false);
      }
    };

    if (classroomId) loadProgress();
    return () => { mounted = false; };
  }, [classroomId, assigned, students.length]);

  const filteredBySearch = (arr) =>
    arr.filter(l => l.title.toLowerCase().includes(search.toLowerCase()));

  const handleCopyCode = async () => {
    if (!classroom?.code) return;
    try {
      await navigator.clipboard.writeText(classroom.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* no-op */ }
  };

  const handleAddLesson = async (lessonId) => {
    try {
      const { error } = await supabase
        .from('classroom_lessons')
        .insert([{ classroom_id: classroomId, lesson_id: lessonId }]);
      if (error) throw error;
      setAssigned(prev => [...prev, lessonId]);
    } catch (e) {
      console.error(e);
      setError('Failed to add lesson.');
    }
  };

  const handleRemoveLesson = async (lessonId) => {
    try {
      const { error } = await supabase
        .from('classroom_lessons')
        .delete()
        .eq('classroom_id', classroomId)
        .eq('lesson_id', lessonId);
      if (error) throw error;
      setAssigned(prev => prev.filter(id => id !== lessonId));
    } catch (e) {
      console.error(e);
      setError('Failed to remove lesson.');
    }
  };

  const handleRemoveStudent = async (studentId) => {
    const ok = window.confirm('Remove this student from the class?');
    if (!ok) return;
    try {
      const { error } = await supabase
        .from('classroom_members')
        .delete()
        .eq('classroom_id', classroomId)
        .eq('user_id', studentId);
      if (error) throw error;
      setStudents(prev => prev.filter(s => s.user_id !== studentId));
    } catch (e) {
      console.error(e);
      setError('Failed to remove student.');
    }
  };

  const handleDeleteClass = async () => {
    const ok = window.confirm('Delete this class and all related records? This cannot be undone.');
    if (!ok) return;
    try {
      await supabase.from('classroom_lesson_progress').delete().eq('classroom_id', classroomId);
      await supabase.from('classroom_lessons').delete().eq('classroom_id', classroomId);
      await supabase.from('classroom_members').delete().eq('classroom_id', classroomId);
      await supabase.from('classrooms').delete().eq('id', classroomId);
      navigate('/classrooms');
    } catch (e) {
      console.error(e);
      setError('Failed to delete class.');
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="h-8 w-1/2 bg-gray-100 rounded animate-pulse" />
        <div className="h-28 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-72 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>
      </div>
    );
  }

  if (!classroom) return null;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-10">
      {/* Header Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{classroom.name}</h1>
          <div className="mt-2 flex items-center gap-3">
            <span className="text-sm text-gray-600">Join Code:</span>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center text-lg font-semibold tracking-widest bg-gray-100 border border-gray-300 rounded px-3 py-1">
                {classroom.code}
              </span>
              <button
                onClick={handleCopyCode}
                className="text-blue-600 text-sm hover:underline"
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`/classrooms/student/${classroom.id}`}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-800 bg-gray-50 hover:bg-gray-100"
          >
            View as Student
          </Link>
          <button
            onClick={() => setEditMode((v) => !v)}
            className="px-4 py-2 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
          >
            {editMode ? 'Done Editing' : 'Edit Lessons'}
          </button>
          <button
            onClick={handleDeleteClass}
            className="px-4 py-2 rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
          >
            Delete Class
          </button>
        </div>
      </div>

      {/* Students Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Students</h2>
          {loadingProgress && <span className="text-xs text-gray-500">Refreshing progress…</span>}
        </div>

        {students.length === 0 ? (
          <p className="text-gray-600">No students yet. Share the join code to invite them.</p>
        ) : (
          <ul className="grid md:grid-cols-2 gap-3">
            {students.map((s) => {
              const map = progress[s.user_id] || {};
              const done = Object.values(map).filter(Boolean).length;
              const total = assigned.length;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;

              return (
                <li key={s.user_id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <Link
                      to={`/profiles/${s.user_id}`}
                      className="font-medium text-blue-700 hover:underline"
                    >
                      {s.profiles?.username || 'Unnamed Student'}
                    </Link>
                    <p className="text-xs text-gray-600 mt-1">
                      Progress: {done} / {total} {total > 0 && `(${pct}%)`}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoveStudent(s.user_id)}
                    className="text-gray-500 hover:text-red-600 text-sm"
                    title="Remove student"
                  >
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Assigned Lessons */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Assigned Lessons</h2>
          {assignedLessons.length > 0 && (
            <span className="text-sm text-gray-600">{assignedLessons.length} total</span>
          )}
        </div>

        {assignedLessons.length === 0 ? (
          <p className="text-gray-600 mt-2">No lessons assigned yet.</p>
        ) : (
          <ul className="mt-3 grid gap-2">
            {assignedLessons.map((l) => (
              <li key={l.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{l.title}</p>
                  <p className="text-xs text-gray-500">Level: {l.level}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/modules/${l.level}/${l.id}?classroomId=${classroom.id}`}
                    className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-500"
                  >
                    Open
                  </Link>
                  {editMode && (
                    <button
                      onClick={() => handleRemoveLesson(l.id)}
                      className="px-3 py-1.5 rounded bg-red-50 text-red-700 text-sm hover:bg-red-100"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Library / Add Lessons */}
      {editMode && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Lesson Library</h2>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search lessons…"
              className="w-64 p-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Folders */}
          <div className="space-y-3">
            {folders.map((f) => {
              const open = !!expandedFolders[f.id];
              const inFolder = lessons.filter(
                (l) =>
                  l.folder_id === f.id &&
                  !assigned.includes(l.id) &&
                  l.title.toLowerCase().includes(search.toLowerCase())
              );

              return (
                <div key={f.id} className="border rounded-lg">
                  <button
                    onClick={() =>
                      setExpandedFolders((prev) => ({ ...prev, [f.id]: !open }))
                    }
                    className="w-full flex items-center justify-between px-4 py-2 text-left"
                  >
                    <span className="font-medium text-gray-900">{f.name}</span>
                    <span className="text-gray-500">{open ? '▾' : '▸'}</span>
                  </button>

                  {open && (
                    <ul className="px-4 pb-3 space-y-2">
                      {inFolder.length === 0 ? (
                        <li className="text-sm text-gray-500">No matching lessons.</li>
                      ) : (
                        inFolder.map((l) => (
                          <li key={l.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                            <span className="truncate">{l.title}</span>
                            <button
                              onClick={() => handleAddLesson(l.id)}
                              className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-500"
                            >
                              Add
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          {/* Uncategorized */}
          <div className="border rounded-lg">
            <div className="px-4 py-2 font-medium text-gray-900">Uncategorized</div>
            <ul className="px-4 pb-3 space-y-2">
              {filteredBySearch(
                uncategorized.filter((l) => !assigned.includes(l.id))
              ).length === 0 ? (
                <li className="text-sm text-gray-500">No matching lessons.</li>
              ) : (
                filteredBySearch(
                  uncategorized.filter((l) => !assigned.includes(l.id))
                ).map((l) => (
                  <li key={l.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                    <span className="truncate">{l.title}</span>
                    <button
                      onClick={() => handleAddLesson(l.id)}
                      className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-500"
                    >
                      Add
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => navigate('/classrooms')}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-800 bg-gray-50 hover:bg-gray-100"
        >
          Back to Classrooms
        </button>
        {editMode && (
          <button
            onClick={() => setEditMode(false)}
            className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-500"
          >
            Done Managing
          </button>
        )}
      </div>
    </div>
  );
}
